import { CURRENCY_MINOR_UNITS, CURRENCY_SYMBOL, type Currency } from './constants.js';

/**
 * Деньги везде живут как целое число минорных единиц (копейки/центы).
 * Так мы никогда не ловим 0.1 + 0.2 = 0.30000000000000004 в ценнике.
 */

export function toMinor(major: number): number {
  return Math.round(major * CURRENCY_MINOR_UNITS);
}

export function toMajor(minor: number): number {
  return minor / CURRENCY_MINOR_UNITS;
}

/**
 * Формат для показа: 12490 ₽, 1 299 000 ₽, $49.99.
 * Копейки прячем, если они нулевые — ценники в магазинах почти всегда целые.
 */
export function formatPrice(minor: number | null | undefined, currency: Currency | null | undefined): string {
  if (minor === null || minor === undefined) return '';
  const code = currency ?? 'RUB';
  const major = toMajor(minor);
  const hasFraction = minor % CURRENCY_MINOR_UNITS !== 0;
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(major);
  return `${formatted} ${CURRENCY_SYMBOL[code] ?? code}`;
}

/**
 * Разбор цены из произвольного текста с сайта.
 *
 * Сложность в том, что разделители разные: «12 490,50 ₽», «12,490.50», «1.299.000",
 * «12490.5», «12 490₽». Стратегия: выкидываем всё кроме цифр и разделителей,
 * затем решаем, какой символ — дробный, а какой — тысячный.
 *
 * Возвращает минорные единицы или null.
 */
export function parsePriceToMinor(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined) return null;

  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw < 0) return null;
    return toMinor(raw);
  }

  /*
   * Число со знаком минус — не цена.
   *
   * Дальше знаки вычищаются вместе с валютой, и «-500» превращалось в честные
   * пятьсот рублей. Так магазин, у которого в разметке стоит скидка «-500»
   * или ошибка в шаблоне, дарил человеку желание с выдуманным ценником.
   * Отрицательной цены не бывает: если она пришла со знаком, значит это
   * не цена товара, а что-то другое.
   *
   * Смотрим на знак перед ПЕРВЫМ числом, где бы оно ни стояло: «скидка -500 ₽»
   * ошибается ровно так же, как голое «-500». А вот диапазон «1 000 - 2 000»
   * не задет: перед первым числом там знака нет.
   */
  const первая = raw.search(/\d/);
  if (первая > 0 && /[-−–—]\s*$/.test(raw.slice(0, первая))) return null;
  if (первая === 0 && /^[-−–—]/.test(raw)) return null;

  // Оставляем цифры, точки, запятые, пробелы (в т.ч. неразрывные) и апострофы (швейцарский разделитель).
  const cleaned = первоеЧисло(raw)
    .replace(/[   ]/g, ' ')
    .replace(/[^\d.,' ]/g, '')
    .trim();

  if (!cleaned || !/\d/.test(cleaned)) return null;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let decimalSep: '.' | ',' | null = null;

  if (lastComma !== -1 && lastDot !== -1) {
    // Тот, что правее — дробный: «1.299,00» (ЕС) против «1,299.00» (US).
    decimalSep = lastComma > lastDot ? ',' : '.';
  } else if (lastComma !== -1) {
    decimalSep = isDecimalSeparator(cleaned, lastComma) ? ',' : null;
  } else if (lastDot !== -1) {
    decimalSep = isDecimalSeparator(cleaned, lastDot) ? '.' : null;
  }

  let normalized: string;
  if (decimalSep) {
    const idx = decimalSep === ',' ? cleaned.lastIndexOf(',') : cleaned.lastIndexOf('.');
    const intPart = cleaned.slice(0, idx).replace(/[^\d]/g, '');
    const fracPart = cleaned.slice(idx + 1).replace(/[^\d]/g, '');
    normalized = `${intPart || '0'}.${fracPart}`;
  } else {
    normalized = cleaned.replace(/[^\d]/g, '');
  }

  const value = Number.parseFloat(normalized);
  if (!Number.isFinite(value) || value < 0) return null;
  /*
   * Отсекаем абсурд: цена больше миллиарда основных единиц — это мусор
   * со страницы, а не дорогой подарок.
   *
   * Предел ровно тот же, что и в проверке схемы (priceMinorSchema, максимум
   * 1 000 000 000_00 копеек), и это не совпадение. Цена, которую нельзя
   * сохранить, хуже отсутствующей: разбор показал бы её в форме, человек
   * нажал бы «Добавить» и получил отказ, не поняв, за что.
   */
  if (value > 1_000_000_000) return null;
  return toMinor(value);
}

/**
 * Первое число строки — со своими разделителями разрядов, но без соседей.
 *
 * Вынесено наружу, потому что то же самое нужно разбору на странице у человека:
 * у Глобуса ценник набран двумя кусками, «719» и «99», и склеенные они дают
 * 71 999 ₽ вместо 719. Правило одно, и жить оно должно в одном месте.
 *
 * Раньше здесь просто выбрасывалось всё, кроме цифр и разделителей, и текст
 * с двумя числами склеивался в одно. Ловилось это не на выдуманных примерах,
 * а на корпусе настоящих страниц: у Zarina надпись «3 999 ₽+ 400 бонусов»
 * превращалась в 3 999 400 рублей, а «или 999.75 ₽ х 4 платежа» — в 999 754.
 * Подарок за миллион вместо четырёх тысяч человек, конечно, заметит, но
 * доверие к разбору уже потеряно.
 *
 * Правило простое и общее: цена — это ПЕРВОЕ число, а всё, что стоит за
 * знаком валюты, словом или плюсом, к ней не относится. Пробел считаем
 * разделителем разрядов, только если за ним ровно три цифры: «7 990» — это
 * одно число, «7990 9990» — два.
 */
export function первоеЧисло(текст: string): string {
  const однимПробелом = текст.replace(/\s+/g, ' ');
  const сырое = /\d[\d.,' ]*/.exec(однимПробелом)?.[0];
  if (!сырое) return '';

  /*
   * Число не кончается разделителем, и хвостовой отрезаем сразу.
   *
   * Правило звучит очевидно, а стоило оно стократной ошибки в цене. Разбор
   * на странице ищет цену во встроенном JSON по образцу
   * `"price"\s*:\s*"?([\d\s.,]+)"?`, и на записи `{"price":54.99,"priceCurrency"…}`
   * образец забирает вместе с числом ЗАПЯТУЮ, которая отделяет его от
   * следующего поля. Дальше срабатывает правило разделителей: после
   * последней запятой цифр нет, значит дробной её не признать, значит обе —
   * и точка, и запятая — считаются разрядными, и «54.99,» читается как 5 499.
   * Живой случай — шорты Nike за 54,99 €: закладка показывала 5 499 €, ровно
   * в сто раз дороже, а серверный разбор те же 54,99 брал из текста страницы
   * и был прав. Хуже того, «1 234,56,» рассыпалось до 1 ₽: первое число
   * обрывалось на единице.
   *
   * Чинить образец на месте было бы починкой одного из вызовов. Хвостовой
   * разделитель не значит ничего НИКОГДА, где бы число ни нашлось, — поэтому
   * правило живёт здесь, в общем разборе, и защищает обоих разборщиков сразу.
   */
  const кусок = сырое.replace(/[.,' ]+$/, '');
  if (!кусок) return '';

  const части = кусок.trim().split(/[ ']+/);
  const собранное = [части[0] ?? ''];
  for (const часть of части.slice(1)) {
    // Ровно три цифры — это следующий разряд того же числа.
    if (/^\d{3}$/.test(часть)) {
      собранное.push(часть);
      continue;
    }
    // Три цифры с копейками — это последний разряд, дальше числа нет.
    if (/^\d{3}[.,]\d{1,2}$/.test(часть)) собранное.push(часть);
    break;
  }
  return собранное.join(' ');
}

/**
 * Разделитель дробный, если после него ровно 1 или 2 цифры и это конец строки.
 * «1,299» — тысячи (3 цифры). «12,5» и «12,50» — дробь.
 */
function isDecimalSeparator(text: string, index: number): boolean {
  const tail = text.slice(index + 1).replace(/[^\d]/g, '');
  const afterRaw = text.slice(index + 1);
  // Если после разделителя есть ещё разделители — он точно тысячный.
  if (/[.,]/.test(afterRaw)) return false;
  return tail.length === 1 || tail.length === 2;
}

/**
 * Знаки валют, по которым число рядом опознаётся как цена.
 *
 * Живёт здесь, вплотную к `detectCurrency`, потому что списка на самом деле
 * два и они обязаны сходиться. Этим ищут ЧИСЛО («перед знаком стоит цена»),
 * а `detectCurrency` по тому же знаку называет валюту. Разъезжались они молча
 * и в обе стороны: браузерный разбор искал цену по «₴», «тг» и «BYN», которых
 * не знал серверный, а серверный — по «₾», «₺» и «¥», которых не было
 * в браузерном. Магазин в Тбилиси отдавал цену одному разбору и не отдавал
 * другому, и понять это можно было, только положив две карточки рядом.
 *
 * Гривна в списке есть намеренно, хотя в валютах приложения её нет. Знак
 * помогает НАЙТИ цену, а назвать валюту он не обязан: пусть человек увидит
 * сумму и выберет валюту сам — это лучше, чем пустая карточка.
 *
 * Строка, а не готовое выражение: её вставляют в состав больших шаблонов
 * с разными окончаниями и флагами.
 */
export const ЗНАКИ_ВАЛЮТ =
  '₽|руб\\.?|р\\.|RUB|RUR|₸|тг|KZT|₾|GEL|֏|AMD|₺|TRY|BYN|₴|грн|\\$|USD|€|EUR|£|GBP|¥|CNY';

/** Символ/код валюты из произвольной строки. */
export function detectCurrency(raw: string | null | undefined): Currency | null {
  if (!raw) return null;
  const text = raw.toLowerCase();
  /*
   * Сокращение «р.» после числа — обычная запись у небольших магазинов.
   *
   * Граница слова \b в JavaScript определена по латинице и цифрам, поэтому
   * с кириллической «р» она не срабатывает вовсе. Вместо неё требуем, чтобы
   * перед «р.» стояла не буква: так «100 р.» распознаётся, а «и др.» — нет.
   */
  if (/₽|руб|rub|rur/.test(text)) return 'RUB';
  if (/(^|[^а-яёa-z])р\./.test(text)) return 'RUB';
  if (/₸|kzt|тенге/.test(text)) return 'KZT';
  /*
   * «тг» — обычное сокращение тенге в казахстанских магазинах, но отдельным
   * словом. Внутри слова оно встречается сплошь и рядом («отгрузка», «Монтгомери»),
   * и без этой оговорки цена рядом с любым таким словом уезжала бы в тенге.
   */
  if (/(^|[^а-яёa-z])тг([^а-яёa-z]|$)/.test(text)) return 'KZT';
  if (/₾|gel|лари/.test(text)) return 'GEL';
  if (/֏|amd|драм/.test(text)) return 'AMD';
  if (/₺|try|лир/.test(text)) return 'TRY';
  if (/byn|бел\.?\s?руб/.test(text)) return 'BYN';
  if (/€|eur/.test(text)) return 'EUR';
  if (/£|gbp/.test(text)) return 'GBP';
  if (/¥|cny|rmb|юан/.test(text)) return 'CNY';
  /*
   * Знак `$` — это доллар, но не всякий. Перед ним ставят букву и другие
   * валюты: `R$` — бразильский реал, `A$` — австралийский доллар,
   * `MX$` — мексиканское песо. Ни одной из них приложение не хранит,
   * и назвать их «USD» хуже, чем не назвать никак: человек увидит
   * осмысленную, но чужую валюту и не заметит подмены.
   *
   * Поэтому доллар — только когда перед `$` НЕ стоит буква. «usd»
   * и слово «доллар» по-прежнему считаются прямо.
   */
  if (/usd|доллар|us\$/.test(text)) return 'USD';
  if (/(^|[^a-zа-яё])\$/.test(text)) return 'USD';
  return null;
}
